import { client } from '@/client';
import {
  PhishingCharacteristicsListResponse,
  SyncPhishingCharacteristicsRequest,
} from '@/client/campaigns';
import { GenericForbiddenApiError } from '@/hooks/query';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';

const listQueryKey = 'phishingCharacteristics';

export const usePhishingCharacteristics = (
  params: UseQueryOptions = {},
): UseQueryResult<PhishingCharacteristicsListResponse> & {
  phishingCharacteristics: PhishingCharacteristicsListResponse | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [listQueryKey],
    () => client.phishingCharacteristics.getPhishingCharacteristics(),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    phishingCharacteristics: data,
    ...(rest as UseQueryResult<PhishingCharacteristicsListResponse>),
  };
};

export const useUpdatePhishingCharacteristicsList = (): {
  update: (
    payload: SyncPhishingCharacteristicsRequest,
  ) => Promise<PhishingCharacteristicsListResponse>;
  isLoading: boolean;
  error: GenericForbiddenApiError | null;
} => {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useMutation<
    PhishingCharacteristicsListResponse,
    GenericForbiddenApiError,
    SyncPhishingCharacteristicsRequest
  >(
    'updatePhishingCharacteristicsList',
    (payload: SyncPhishingCharacteristicsRequest) =>
      client.phishingCharacteristics.updatePhishingCharacteristicsList(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([listQueryKey]);
      },
    },
  );

  return { update: mutateAsync, ...(rest as any) };
};
